import { baseApi } from "./baseApi";
import type { MyPermissions, UserWithAssignments } from "../../../shared/types/rbac";
import type { ICurrentUser } from "../../views/settings/security/security.interface";
import { isAdminRole, grantsPlatformAdmin, scopeOf } from "../../../shared/roles";
import type { DataScope } from "../../../shared/types/accessScope";
import type { ProductKey } from "../../../shared/products/keys";

// Types for RBAC API
export interface RbacRole {
  id: string;
  name: string;
  label: string;
  description: string | null;
  category: string;
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
  tenantScopeId: string | null;
  isSystem: boolean;
  isHidden: boolean;
  isImmutable: boolean;
  /** public.roles.data_scope (D10). */
  dataScope: DataScope;
  /** public.roles.product_key: which product's catalog the role belongs to. */
  productKey: ProductKey;
  /**
   * People in this instance who hold the role. Null when the caller can't be told
   * (they don't reach the whole instance, or no instance was named).
   */
  holderCount?: number | null;
}

/**
 * `GET /admin/rbac/users/:email/impact?instanceId`: what removing or blocking this
 * person leaves behind in the instance. Needs users.manage and whole-instance reach.
 */
export interface UserImpact {
  email: string;
  /** Jobs that name them as recruiter. */
  recruiterOn: number;
  /** Jobs that name them as hiring manager (Phase J2, D1). Absent from an older server. */
  hiringManagerOn?: number;
  /** Job teams they're on. */
  teamOn: number;
  /** Job access lists they're on. */
  accessListOn: number;
  /** Candidates they're assigned to interview. */
  interviewerFor: number;
  /** Enabled AI Employee tasks that run as them. */
  agentTasks: number;
}

export interface AccessMatrixUser {
  email: string;
  displayName: string | null;
  status: string;
  assignments: UserWithAssignments["assignments"];
}

// The wire shape lives in shared/ so skyPerformance can type its own my-permissions query without
// importing this hire endpoint module (spec §2 import boundaries).
export type { MyPermissions } from "../../../shared/types/rbac";

// Request types
export interface CreateUserRequest {
  email: string;
  displayName?: string;
  assignments: {
    roleId: string;
    tenantId?: string;
    instanceId?: string;
    /** The role's product (spec §4.4); omitted means hire. */
    productKey?: ProductKey;
  }[];
}

export interface UpdateUserRequest {
  email: string;
  displayName?: string;
  add?: { roleId: string; tenantId?: string; instanceId?: string; productKey?: ProductKey }[];
  remove?: string[];
  update?: { assignmentId: string; roleId: string; productKey?: ProductKey }[];
}

export interface ApplicantAccessUser {
  displayName: string | null;
  email: string;
  roleLabel: string;
}

export interface ApplicantAccessResponse {
  roles: { name: string; label: string }[];
  users: ApplicantAccessUser[];
}

export interface CreateRoleRequest {
  name: string;
  label: string;
  description?: string;
  category: "Admin" | "Manager" | "Staff" | "Viewer";
  permissions: Record<string, boolean>;
  navigation: Record<string, boolean>;
}

/** Map RBAC user to ICurrentUser shape for backward compat with leads components */
function mapToCurrentUser(user: UserWithAssignments, index: number): ICurrentUser {
  const isAdmin = user.assignments.some((a) => isAdminRole(a.roleName));
  const firstName = user.displayName?.split(" ")[0] || "";
  const lastName = user.displayName?.split(" ").slice(1).join(" ") || "";
  return {
    email: user.email,
    enabled: user.status === "active",
    firstname: firstName,
    fullname: user.displayName || user.email,
    id: String(user.id),
    index,
    isMultiTenant: false,
    isMultiInstance: false,
    // By the role AND its scope: a legacy tenant- or instance-scoped super_admin
    // is not platform staff. (Platform staff are left out of this list anyway.)
    isPlatformAdmin: user.assignments.some((a) =>
      grantsPlatformAdmin(a.roleName, scopeOf(a.tenantId, a.instanceId)),
    ),
    isTenantAdmin: isAdmin,
    lastname: lastName,
    invitationStatus: "",
    objectid: String(user.id),
    roles: user.assignments[0]?.roleLabel || "User",
    roleNames: user.assignments.map((a) => a.roleName),
    securitygroups: [],
    selected: false,
    shortName: firstName ? `${firstName[0]}${lastName?.[0] || ""}` : user.email[0],
    source: "local",
    type: "user",
    status: user.status,
    updatedDate: "",
  };
}

export const rbacApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Role Management
    getRoles: builder.query<{ roles: RbacRole[] }, void>({
      query: () => "/admin/rbac/roles",
      providesTags: [{ type: "RBAC", id: "ROLES" }],
    }),

    updateRoleConfig: builder.mutation<
      { role: RbacRole },
      {
        roleId: string;
        /**
         * Display name only. `roles.name` is the frozen join key — UAM
         * publishes it as `role.key` and skySupport stores it — so renaming a
         * role changes what people read, never what anything joins on.
         */
        label?: string;
        permissions?: Record<string, boolean> | null;
        navigation?: Record<string, boolean> | null;
      }
    >({
      query: ({ roleId, ...body }) => ({
        url: `/admin/rbac/roles/${roleId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ROLES" },
        { type: "RBAC", id: "MATRIX" },
      ],
    }),

    resetRoleConfig: builder.mutation<{ success: boolean; message: string }, string>({
      query: (roleId) => ({
        url: `/admin/rbac/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "ROLES" },
        { type: "RBAC", id: "MATRIX" },
      ],
    }),

    createRole: builder.mutation<{ role: RbacRole }, CreateRoleRequest>({
      query: (body) => ({
        url: "/admin/rbac/roles",
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "RBAC", id: "ROLES" }],
    }),

    deleteRole: builder.mutation<{ success: boolean }, string>({
      query: (roleId) => ({
        url: `/admin/rbac/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "RBAC", id: "ROLES" }],
    }),

    // User Management
    getUsers: builder.query<
      { users: UserWithAssignments[] },
      { tenantId?: string; instanceId?: string } | void
    >({
      query: (args) => {
        const tenantId = args && "tenantId" in args ? args.tenantId : undefined;
        const instanceId = args && "instanceId" in args ? args.instanceId : undefined;
        const params = new URLSearchParams();
        if (tenantId) params.set("tenantId", tenantId);
        if (instanceId) params.set("instanceId", instanceId);
        const qs = params.toString();
        return qs ? `/admin/rbac/users?${qs}` : "/admin/rbac/users";
      },
      providesTags: [{ type: "RBAC", id: "USERS" }],
    }),

    createUser: builder.mutation<{ user: UserWithAssignments }, CreateUserRequest>({
      query: (body) => ({
        url: "/admin/rbac/users",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        // A role change can clear or flag access scope, and changes who holds groups and departments.
        "UserScope",
        "LocationGroups",
        "Departments",
      ],
    }),

    updateUser: builder.mutation<{ user: UserWithAssignments }, UpdateUserRequest>({
      query: ({ email, ...body }) => ({
        url: `/admin/rbac/users/${encodeURIComponent(email)}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        // A role change can clear or flag access scope, and changes who holds groups and departments.
        "UserScope",
        "LocationGroups",
        "Departments",
      ],
    }),

    blockUser: builder.mutation<{ success: boolean }, string>({
      query: (email) => ({
        url: `/admin/rbac/users/${encodeURIComponent(email)}/block`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
      ],
    }),

    unblockUser: builder.mutation<{ success: boolean }, string>({
      query: (email) => ({
        url: `/admin/rbac/users/${encodeURIComponent(email)}/unblock`,
        method: "POST",
      }),
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
      ],
    }),

    deleteUser: builder.mutation<
      { success: boolean; userDeleted?: boolean; removedAssignments?: number },
      { email: string; tenantId?: string; instanceId?: string; includeTenantWide?: boolean }
    >({
      query: ({ email, tenantId, instanceId, includeTenantWide }) => {
        const params = new URLSearchParams();
        if (tenantId) params.set("tenantId", tenantId);
        if (instanceId) params.set("instanceId", instanceId);
        // Opt-in flag for also dropping tenant-wide access — the server rejects
        // the delete with TENANT_WIDE_ASSIGNMENT until the admin confirms it.
        if (includeTenantWide) params.set("includeTenantWide", "true");
        const qs = params.toString();
        return {
          url: `/admin/rbac/users/${encodeURIComponent(email)}${qs ? `?${qs}` : ""}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [
        { type: "RBAC", id: "USERS" },
        { type: "RBAC", id: "MATRIX" },
        { type: "RBAC", id: "PLATFORM_USERS" },
        // A role change can clear or flag access scope, and changes who holds groups and departments.
        "UserScope",
        "LocationGroups",
        "Departments",
      ],
    }),

    // Access Matrix
    getAccessMatrix: builder.query<{ accessMatrix: AccessMatrixUser[] }, void>({
      query: () => "/admin/rbac/permissions/access-matrix",
      providesTags: [{ type: "RBAC", id: "MATRIX" }],
    }),

    // Current User Permissions (instanceId must be passed explicitly since /admin/rbac is exempt).
    // A string or undefined is the hire form every existing caller uses: same URL and cache key as
    // before. `productKey` asks for another product's answer; a UI hint only, the server gates
    // every product route itself.
    getMyPermissions: builder.query<
      MyPermissions,
      string | undefined | { instanceId?: string; productKey?: ProductKey }
    >({
      query: (arg) => {
        const { instanceId, productKey } =
          typeof arg === "object" && arg !== null
            ? arg
            : { instanceId: arg, productKey: undefined };
        const params = new URLSearchParams();
        if (instanceId) params.set("instanceId", instanceId);
        if (productKey) params.set("productKey", productKey);
        const qs = params.toString();
        return `/admin/rbac/permissions/my-permissions${qs ? `?${qs}` : ""}`;
      },
      providesTags: [{ type: "RBAC", id: "MY_PERMISSIONS" }],
    }),

    // Platform Users — backed by local RBAC users endpoint
    getPlatformUsers: builder.query<{ users: ICurrentUser[] }, void>({
      query: () => "/admin/rbac/users",
      transformResponse: (response: { users: UserWithAssignments[] }) => ({
        users: response.users.map(mapToCurrentUser),
      }),
      providesTags: [{ type: "RBAC", id: "PLATFORM_USERS" }],
    }),

    // What removing or blocking someone leaves behind, for the confirm dialogs.
    getUserImpact: builder.query<UserImpact, { email: string; instanceId: string }>({
      query: ({ email, instanceId }) => ({
        url: `/admin/rbac/users/${encodeURIComponent(email)}/impact`,
        params: { instanceId },
      }),
    }),

    // Applicant access — users/roles with applicants.view permission for an instance
    getApplicantAccess: builder.query<ApplicantAccessResponse, string>({
      query: (instanceId) => `/admin/rbac/permissions/applicant-access?instanceId=${instanceId}`,
      providesTags: [{ type: "RBAC", id: "USERS" }],
    }),

    // Search platform users — client-side filter over local RBAC users
    searchPlatformUsers: builder.query<ICurrentUser[], string>({
      query: () => "/admin/rbac/users",
      transformResponse: (response: { users: UserWithAssignments[] }, _meta, search: string) => {
        const term = search.toLowerCase();
        return response.users
          .filter(
            (u) =>
              u.email.toLowerCase().includes(term) ||
              (u.displayName && u.displayName.toLowerCase().includes(term)),
          )
          .map(mapToCurrentUser);
      },
    }),
  }),
});

export const {
  useGetRolesQuery,
  useUpdateRoleConfigMutation,
  useResetRoleConfigMutation,
  useCreateRoleMutation,
  useDeleteRoleMutation,
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useDeleteUserMutation,
  useGetAccessMatrixQuery,
  useGetMyPermissionsQuery,
  useGetApplicantAccessQuery,
  useLazyGetUserImpactQuery,
  useGetPlatformUsersQuery,
  useSearchPlatformUsersQuery,
} = rbacApi;
